interface CacheEntry {
  data: any
  skusKey: string
  timestamp: number
  version: number
}

const CACHE_KEY_PREFIX = "inventory_cache_"
const CACHE_TTL = 20 * 60 * 1000 // 20 minutes
const CACHE_VERSION = 1 // Increment to invalidate old cache

/**
 * Clear all inventory cache entries
 */
export function clearAllInventoryCache() {
  try {
    const keys = Object.keys(localStorage)
    keys.forEach((key) => {
      if (key.startsWith(CACHE_KEY_PREFIX)) {
        localStorage.removeItem(key)
      }
    })
  } catch (error) {
    console.warn("Cache clear error:", error)
  }
}

/**
 * Remove expired cache entries
 */
export function clearExpiredCache() {
  try {
    const keys = Object.keys(localStorage)
    const now = Date.now()

    keys.forEach((key) => {
      if (key.startsWith(CACHE_KEY_PREFIX)) {
        try {
          const item = localStorage.getItem(key)
          if (!item) return

          const entry: CacheEntry = JSON.parse(item)
          // Remove if expired or wrong version
          if (
            now - entry.timestamp > CACHE_TTL ||
            entry.version !== CACHE_VERSION
          ) {
            localStorage.removeItem(key)
          }
        } catch {
          // Invalid entry, remove it
          localStorage.removeItem(key)
        }
      }
    })
  } catch (error) {
    console.warn("Cache cleanup error:", error)
  }
}

/**
 * Get cached inventory data by SKUs key
 * Returns null if not found or expired
 */
export function getCachedInventory(skusKey: string) {
  try {
    const cacheKey = CACHE_KEY_PREFIX + skusKey
    const cached = localStorage.getItem(cacheKey)
    if (!cached) return null

    const entry: CacheEntry = JSON.parse(cached)
    const age = Date.now() - entry.timestamp

    // Check version - invalidate if outdated
    if (entry.version !== CACHE_VERSION) {
      localStorage.removeItem(cacheKey)
      return null
    }

    // Check if expired
    if (age > CACHE_TTL) {
      localStorage.removeItem(cacheKey)
      return null
    }

    return entry.data
  } catch (error) {
    console.warn("Inventory cache read error:", error)
    return null
  }
}

/**
 * Cache inventory data with timestamp
 */
export function setCachedInventory(skusKey: string, data: any) {
  try {
    const cacheKey = CACHE_KEY_PREFIX + skusKey
    const entry: CacheEntry = {
      data,
      skusKey,
      timestamp: Date.now(),
      version: CACHE_VERSION,
    }
    const serialized = JSON.stringify(entry)
    localStorage.setItem(cacheKey, serialized)
  } catch (error) {
    // Storage quota exceeded - clear ALL inventory cache and retry
    if (error.name === "QuotaExceededError") {
      console.warn(
        "[Cache] Quota exceeded, clearing all inventory cache and retrying..."
      )
      clearAllInventoryCache()
      try {
        const cacheKey = CACHE_KEY_PREFIX + skusKey
        const entry: CacheEntry = {
          data,
          skusKey,
          timestamp: Date.now(),
          version: CACHE_VERSION,
        }
        const serialized = JSON.stringify(entry)
        localStorage.setItem(cacheKey, serialized)
      } catch (retryError) {
        console.warn(
          `[Cache] Write failed after clearing all cache: ${retryError.message}`
        )
      }
    } else {
      console.warn("Inventory cache write error:", error)
    }
  }
}
